import { useMemo, PropsWithChildren, useCallback, useEffect } from "react";
import { useStoredState, useAxios } from "../../hooks";
import { AuthContext, AuthState, IAuthContext, defaultAuthState } from ".";
import { Endpoints } from "../../api";
import { MfaToken, ResetPasswordRequest } from "../../models";
import { useQueryClient } from "@tanstack/react-query";

export const AuthProvider = (props: PropsWithChildren) => {
    const { post } = useAxios();
    const queryClient = useQueryClient();
    const [authState, setAuthState] = useStoredState<AuthState>("auth", defaultAuthState);
    const [mfaTokens, setMfaTokens] = useStoredState<MfaToken[]>("mfa", []);
    const [isPublicComputer, setIsPublicComputer] = useStoredState<boolean>("publicComputer", true);
    const { decodedAccessToken, isAuthenticated } = authState;

    const isAuthorized = useMemo<boolean>(
        () => isAuthenticated && !!decodedAccessToken.tenant_id,
        [isAuthenticated, decodedAccessToken]
    );

    useEffect(() => {
        queryClient.invalidateQueries();
    }, [queryClient, isAuthorized]);

    const getMfaToken = useCallback(
        (email: string): MfaToken | undefined =>
            mfaTokens.find((token) => token.email.toLowerCase() === email.toLowerCase()),
        [mfaTokens]
    );

    const setMfaToken = useCallback(
        (mfaToken: MfaToken) => {
            setMfaTokens((prev) => [
                ...prev.filter((token) => token.email.toLowerCase() !== mfaToken.email.toLowerCase()),
                mfaToken,
            ]);
        },
        [setMfaTokens]
    );

    const deleteMfaToken = useCallback(
        (email: string) => {
            setMfaTokens((prev) => prev.filter((token) => token.email.toLowerCase() !== email.toLowerCase()));
        },
        [setMfaTokens]
    );

    const forgotPassword = useCallback(
        async (emailAddress: string) => {
            return await post<string, void>({
                url: Endpoints.ForgotPassword,
                data: emailAddress,
                config: { headers: { "Content-Type": "application/json" } },
            });
        },
        [post]
    );

    const resetPassword = useCallback(
        async (resetPasswordRequest: ResetPasswordRequest) => {
            return await post<ResetPasswordRequest, void>({ url: Endpoints.ResetPassword, data: resetPasswordRequest });
        },
        [post]
    );

    const value = useMemo<IAuthContext>(
        () => ({
            ...authState,
            isAuthorized,
            setAuthState,
            authState,
            isPublicComputer,
            setIsPublicComputer,
            getMfaToken,
            setMfaToken,
            deleteMfaToken,
            forgotPassword,
            resetPassword,
        }),
        [
            authState,
            isAuthorized,
            isPublicComputer,
            setIsPublicComputer,
            setAuthState,
            getMfaToken,
            setMfaToken,
            deleteMfaToken,
            forgotPassword,
            resetPassword,
        ]
    );

    return <AuthContext.Provider value={value} {...props} />;
};
